import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';

import { useSetEntityRoleScope } from '@/api/entity-roles/mutations';
import { _getEntityRole, _getPermissionCatalog } from '@/api/entity-roles/queries';
import { CatalogGroup, CatalogPermission, PermissionAssignment } from '@/api/entity-roles/types';
import { Permission } from '@/api/permissions';
import { useCreateRoleRequest, useRefreshRoleRequestCaches } from '@/api/role-requests/mutations';
import { type EntityRoleRequest, RoleRequestType } from '@/api/role-requests/types';
import { Loader, theme, Typography } from '@/shared/design-system';
import {
  ADDRESS_BOOK_GROUP_KEY,
  FULL_VIEW_PERMISSION,
  withoutFullViewRow,
} from '@/shared/modules/role-board/category-scope';
import { CategoryScopeControl } from '@/shared/modules/role-board/components/category-scope-control';
import { DraftSaveBar } from '@/shared/modules/role-board/components/permission-draft/draft-save-bar';
import { ReviewChangesModal } from '@/shared/modules/role-board/components/permission-draft/review-changes-modal';
import { PERMISSION_FILTERS } from '@/shared/modules/role-board/components/permission-picker';
import { RoleScopeKind } from '@/shared/modules/role-board/types';
import { useAugmentedCatalogGroups } from '@/shared/modules/role-board/use-augmented-catalog-groups';
import { usePermissionDraft } from '@/shared/modules/role-board/use-permission-draft';
import { useRoleChangeBatch } from '@/shared/modules/role-board/use-role-change-batch';
import {
  BYPASS_GROUP_KEYS,
  canGrantProtectedPermissions,
  countPermissions,
  formatPermissionSummary,
  PermissionSelectionMap,
  toRoleSelection,
} from '@/utils/permission-catalog';
import {
  countDraftChanges,
  describePermissionDraft,
  type PermissionDraftSummary,
  toPermissionChangeRequests,
} from '@/utils/permission-draft';
import { pendingPermissionChanges } from '@/utils/role-request-activity';

import { isOwnerRole } from '../common';
import { PermissionPicker } from '../components/permission-picker';
import { CenteredState } from '../styled';
import { useCallerIsOwner } from '../use-caller-is-owner';
import { useRoleAdminAccess } from '../use-role-admin-access';

export const LOAD_ERROR_MESSAGE = "Couldn't load this role's permissions";

export type RolePermissionsTabProps = {
  entityId?: string | null;
  roleId: string;
  /** Requests still waiting for a decision; their rows are marked and frozen. */
  pendingRequests?: readonly EntityRoleRequest[];
  /** The caller's own requests settle on submit, so the review says "applies now". */
  canApprove?: boolean;
};

const EMPTY_SELECTION: PermissionSelectionMap = new Map();
const PINNED_GROUPS: ReadonlySet<string> = new Set([ADDRESS_BOOK_GROUP_KEY]);

export const RolePermissionsTab: FC<RolePermissionsTabProps> = ({
  entityId,
  roleId,
  pendingRequests = [],
  canApprove = false,
}) => {
  const { heldPermissions, canManage } = useRoleAdminAccess();
  const isOwner = useCallerIsOwner(entityId);

  const catalog = useQuery(_getPermissionCatalog());
  const role = useQuery(_getEntityRole(entityId, roleId));

  const selection = useMemo<PermissionSelectionMap>(
    () =>
      role.data ? toRoleSelection(role.data.permissions, role.data.isLocked) : EMPTY_SELECTION,
    [role.data],
  );

  const present = useMemo(
    () => new Set((role.data?.permissions ?? []).map((row) => row.permission)),
    [role.data],
  );

  const augmented = useAugmentedCatalogGroups({ groups: catalog.data?.groups, present });
  const groups = useMemo(() => withoutFullViewRow(augmented), [augmented]);

  const pending = useMemo(
    () => pendingPermissionChanges(pendingRequests, roleId),
    [pendingRequests, roleId],
  );

  const { draft, effective, toggle, stageMany, keepOnly, discard } = usePermissionDraft(
    roleId,
    selection,
    { pending, ready: Boolean(role.data) },
  );
  const [isReviewOpen, setReviewOpen] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<PermissionDraftSummary | null>(null);

  const { mutateAsync: submitRequest } = useCreateRoleRequest({ entityId, silent: true });
  const { mutateAsync: requestRoleChangeAsync } = useCreateRoleRequest({ entityId });
  const refreshRoleCaches = useRefreshRoleRequestCaches({ entityId });
  const { run: submitBatch, isSubmitting } = useRoleChangeBatch(submitRequest, refreshRoleCaches);
  const { mutateAsync: saveScope, isPending: savingScope } = useSetEntityRoleScope({ entityId });

  const handleChange = (permission: CatalogPermission, granted: boolean) => {
    if (role.data?.isLocked) return;

    toggle(permission.permission, granted);
  };

  const handleBulkChange = (
    _group: CatalogGroup,
    add: PermissionAssignment[],
    remove: Permission[],
  ) => {
    if (role.data?.isLocked) return;

    stageMany(
      add.map(({ permission }) => permission),
      remove,
    );
  };

  const confirmDraft = async () => {
    // The review lists what is being sent, not what the draft turns into while it is sent.
    setSubmittedSummary(describePermissionDraft(draft, groups));
    const result = await submitBatch(toPermissionChangeRequests(roleId, draft, pending));
    setSubmittedSummary(null);
    if (!result) return;

    const { failed } = result;
    keepOnly(
      new Set(
        failed.flatMap((input) =>
          'permission' in input.payload ? [input.payload.permission] : [],
        ),
      ),
    );
    setReviewOpen(false);
  };

  // `isPending`, not `isLoading`: a query still waiting on its `enabled` guard is
  // pending without fetching, and rendering that as a failure is a lie.
  if (catalog.isPending || role.isPending) {
    return (
      <CenteredState>
        <Loader />
      </CenteredState>
    );
  }

  if (catalog.isError || role.isError || !catalog.data || !role.data) {
    return (
      <CenteredState>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {LOAD_ERROR_MESSAGE}
        </Typography>
      </CenteredState>
    );
  }

  const counts = countPermissions(groups, effective);
  const currentRole = role.data;
  const changeCounts = countDraftChanges(draft);
  const editable = canManage && !currentRole.isLocked;

  return (
    <>
      <PermissionPicker
        // Each role opens on its own default filter rather than inheriting the last role's.
        key={roleId}
        groups={groups}
        selection={effective}
        bulkAddBlockedGroups={BYPASS_GROUP_KEYS}
        canGrantProtected={canGrantProtectedPermissions({ isOwner, heldPermissions })}
        editable={editable && !isSubmitting}
        onChange={handleChange}
        onBulkChange={canManage ? handleBulkChange : undefined}
        summary={formatPermissionSummary(counts)}
        draft={draft}
        pending={pending}
        showFilter
        pinnedGroups={PINNED_GROUPS}
        defaultFilter={editable ? PERMISSION_FILTERS.all : PERMISSION_FILTERS.granted}
        renderGroupIntro={(group) =>
          group.key === ADDRESS_BOOK_GROUP_KEY ? (
            <CategoryScopeControl
              role={currentRole}
              scopable={!isOwnerRole(currentRole)}
              canManage={canManage}
              queryIdentity={`entity:${entityId ?? ''}`}
              saving={savingScope}
              onRequestAll={() =>
                requestRoleChangeAsync({
                  type: RoleRequestType.AddRolePermission,
                  payload: { roleId, permission: FULL_VIEW_PERMISSION },
                }).then(() => undefined)
              }
              onRequestSelected={() =>
                requestRoleChangeAsync({
                  type: RoleRequestType.RemoveRolePermission,
                  payload: { roleId, permission: FULL_VIEW_PERMISSION },
                }).then(() => undefined)
              }
              onSave={(resourceIds) =>
                saveScope({ roleId, kind: RoleScopeKind.AddressBookCategory, resourceIds })
              }
            />
          ) : null
        }
      />

      {changeCounts.total > 0 && (
        <DraftSaveBar
          added={changeCounts.added}
          removed={changeCounts.removed}
          memberCount={currentRole.memberCount}
          disabled={isSubmitting}
          onDiscard={discard}
          onReview={() => setReviewOpen(true)}
        />
      )}

      <ReviewChangesModal
        open={isReviewOpen}
        roleName={currentRole.name}
        summary={submittedSummary ?? describePermissionDraft(draft, groups)}
        memberCount={currentRole.memberCount}
        appliesImmediately={canApprove}
        isSubmitting={isSubmitting}
        onClose={() => setReviewOpen(false)}
        onConfirm={() => void confirmDraft()}
      />
    </>
  );
};
